import {
    DivisibleReceiver,
    type DivisibleInjectionKey,
    defaultDivisibleRemote,
} from '@precon-tools-suite-by-shared/vue-divisibles';
import {
    type OpenDialogProps,
    pickDialogProps,
    dialogPropListeners,
} from './props';
import { useDialog } from 'primevue/usedialog';
import type {
    DynamicDialogCloseOptions,
    DynamicDialogInstance,
} from 'primevue/dynamicdialogoptions';
import { usePartialComponent } from '@precon-tools-suite-by-shared/vue-partials';
import {
    type Component,
    type MaybeRefOrGetter,
    reactive,
    nextTick,
    defineComponent,
    provide,
    onUnmounted,
    h,
    mergeProps,
} from 'vue';
import Skeleton from 'primevue/skeleton';
import Button from 'primevue/button';
import { useReceiverUserConfigStore } from '../stores/receiverUserConfig';

export interface OpenDialogOptions extends OpenDialogProps {
    dialogProps?: Record<string, any>;
    partialProps?: Record<string, any>;
    partialSlots?: Record<string, (...args: any[]) => any>;
    data?: Record<string, any>;
    receiverKey?: DivisibleInjectionKey;
    onClose?: (options: DynamicDialogCloseOptions | undefined) => void;
    onOpen?: (dialog: DynamicDialogInstance) => void;
    title?: string;
    subtitle?: string;
    dismissableMask?: boolean;
    closeOnEscape?: boolean;
}

const dialogSkeletons = {
    title: () =>
        h(Skeleton, {
            style: {
                width: '6rem',
                height: '2rem',
                display: 'inline-block',
            },
        }),
    subtitle: () =>
        h(Skeleton, {
            style: {
                width: '5rem',
                height: '1.4rem',
                display: 'inline-block',
            },
        }),
    footer: () => [
        h(Skeleton, {
            style: {
                width: '7rem',
                'max-width': '50%',
                'margin-bottom': '0.25rem',
            },
        }),
        h(Skeleton, {
            style: {
                width: '14rem',
                'max-width': '80%',
            },
        }),
    ],
    actions: () => [
        h(Skeleton, {
            style: {
                height: '2.2rem',
                width: '8rem',
            },
            class: ['mr-2'],
        }),
        h(Skeleton, {
            style: {
                height: '2.2rem',
                width: '6rem',
            },
        }),
    ],
    tools: () => [
        h(Button as any, {
            class: 'p-button-rounded p-button-text p-button-secondary',
            icon: 'pi pi-spinner pi-spin',
            disabled: true,
        }),
    ],
};

export function usePartialDialog(
    partial: MaybeRefOrGetter<Component | string | null | undefined>,
    fallbackPartial?: MaybeRefOrGetter<Component | string | null | undefined>
) {
    const component = usePartialComponent(partial, {
        fallback: fallbackPartial,
    });
    const { open: openDialog } = useDialog();
    const config = useReceiverUserConfigStore();

    const open = (options?: OpenDialogOptions) => {
        const receiver = new DivisibleReceiver();

        const title = receiver.use('title');
        const subtitle = receiver.use('subtitle');
        const header = receiver.use('header');
        const tools = receiver.use('tools');
        const footer = receiver.use('footer');
        const actions = receiver.use('actions');

        const defaultOptions = {
            modal: true,
            closable: true,
            maximizable: false,
            breakpoints: { '960px': '75vw' },
            style: { width: '40vw' },
            showFooter: true,
        };

        options = {
            ...defaultOptions,
            ...options,
            dismissableMask: options?.dismissableMask ?? config.dismissableMask,
            closeOnEscape: options?.closeOnEscape ?? config.closeOnEscape,
        };

        const dialogProps = reactive<OpenDialogProps>(pickDialogProps(options));

        const listeners = {
            ...dialogPropListeners(dialogProps, options),
            [`onDialog:close`]: (response: unknown) =>
                nextTick(() => dialog.close(response)),
        };

        const receiverKey = options?.receiverKey ?? defaultDivisibleRemote;

        const dialogReceiverComponent = defineComponent(
            (_props, { slots, attrs }) => {
                provide(receiverKey, receiver.remote());

                onUnmounted(() => receiver.close());

                return () =>
                    h(
                        component.value,
                        mergeProps(
                            listeners,
                            attrs,
                            options?.partialProps ?? {}
                        ),
                        { ...slots, ...(options?.partialSlots ?? {}) }
                    );
            }
        );

        const dialog = openDialog(dialogReceiverComponent, {
            props: dialogProps,
            templates: {
                header: () => {
                    if (header.exists.value) return h(header.component);

                    return [
                        h('span', { class: 'p-dialog-title mr-auto' }, [
                            options?.title ??
                                h(
                                    title.component,
                                    {},
                                    {
                                        skeleton: dialogSkeletons.title,
                                    }
                                ),
                            h(
                                'span',
                                {
                                    style: 'font-weight:normal; font-size: smaller; opacity: 0.6',
                                    class: 'ml-1',
                                },
                                options?.subtitle ??
                                    h(
                                        subtitle.component,
                                        {},
                                        {
                                            skeleton: dialogSkeletons.subtitle,
                                        }
                                    )
                            ),
                        ]),
                        h(
                            tools.component,
                            {},
                            {
                                skeleton: dialogSkeletons.tools,
                            }
                        ),
                    ];
                },
                footer: dialogProps.showFooter
                    ? () =>
                          h(
                              'div',
                              {
                                  class: 'flex align-items-center justify-content-end mt-2',
                              },
                              [
                                  h(
                                      'div',
                                      {
                                          class: 'mr-auto text-left',
                                      },
                                      h(
                                          footer.component,
                                          {},
                                          {
                                              skeleton: dialogSkeletons.footer,
                                          }
                                      )
                                  ),
                                  h(
                                      actions.component,
                                      {},
                                      {
                                          skeleton: dialogSkeletons.actions,
                                      }
                                  ),
                              ]
                          )
                    : undefined,
            },
            data: options.data,
            onClose: options.onClose,
        });

        if (options?.onOpen) options.onOpen(dialog);

        return dialog;
    };

    const run = (options?: OpenDialogOptions) =>
        new Promise<DynamicDialogCloseOptions | undefined>((resolve) =>
            open({
                ...(options ?? {}),
                onClose(response?: DynamicDialogCloseOptions) {
                    const o = options?.onClose;
                    if (o) o(response);
                    resolve(response);
                },
            })
        );

    return { component, open, run };
}
